import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbSchema, faqSchema, personSchema, webPageSchema, PRESS_FEATURES } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";
import { NetworkNextStep } from "@/components/NetworkNextStep";
const headshot = "/img/headshot.webp";
const speakingPhoto = "/img/speaking-photo.webp";
const bookCover = "/img/book-cover-front.webp";

const MICROPHONE_SRC = "/landing/media-microphone.webp";
const MICROPHONE_ALT =
  "A vintage broadcast condenser microphone on a dark wooden desk in a small pool of warm tungsten light, evoking podcast and press interviews.";

export default function Media() {
  const SEO_TITLE =
    "Media & Press Resources, Shariq Refai, MD, MBA";
  const SEO_DESCRIPTION =
    "Press resources for board-certified psychiatrist Shariq Refai, MD, MBA: bios, credentials, headshots, a quote library, and how to request an interview.";

  const page = webPageSchema({
    path: "/media",
    title: SEO_TITLE,
    description: SEO_DESCRIPTION,
    image: "/og-media.jpg",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Media & Press", path: "/media" },
    ],
    speakableSelectors: [".quick-answer"],
  });

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url="/media"
        image="/og-media.jpg"
        keywords="Shariq Refai press, media kit, psychiatrist media commentary, podcast guest psychiatrist, shrinkMD founder press"
        schema={[
          personSchema,
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Media & Press", path: "/media" },
          ]),
          faqSchema([
            {
              question: "Is Dr. Refai available for interviews?",
              answer:
                "Yes. Dr. Refai accepts media interview requests. Use the contact form with 'Media & Press' selected as the inquiry type, or email media@shariqrefai.com. Inquiries are reviewed within three business days.",
            },
            {
              question: "What topics does Dr. Refai speak on for media?",
              answer:
                "Anxiety, overthinking, the high-performer pattern, sports and performance psychiatry, telepsychiatry, psychiatric medication, workplace mental health, mental health policy, and the intersection of psychiatry and technology.",
            },
            {
              question: "How do I request high-resolution photos?",
              answer:
                "High-resolution headshots and stage photos are available for download from the Media page. Please credit 'Shariq Refai, MD, MBA' with a link to shariqrefai.com when published.",
            },
            {
              question:
                "What's Dr. Refai's preferred turnaround time for media requests?",
              answer:
                "Inquiries are reviewed within three business days. For tight deadlines, please indicate the deadline clearly in the inquiry. Pre-arranged commentary on developing news topics is available for vetted publications.",
            },
            {
              question: "Does Dr. Refai do podcast appearances?",
              answer:
                "Yes. Dr. Refai is available for podcast appearances on mental health, psychiatry, performance, leadership, and digital health. Submit through the contact form with 'Media & Press' selected.",
            },
          ]),
        ]}
      />

      {/* H1 */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Media & Press" },
            ]}
          />
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
            <div className="md:col-span-7 order-2 md:order-1">
              {/* Credibility eyebrow */}
              <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-accent">
                Media Kit · Shariq Refai, MD, MBA, FAPA · Board-Certified Psychiatrist
              </p>
              {/* Outcome-led H1: what a journalist needs answered */}
              <h1 className="mt-4 text-3xl md:text-5xl font-serif text-primary leading-[1.15] tracking-tight">
                Bios, headshots, quotes, and booking. All in one place.
              </h1>
              {/* Subhead: audience + mechanism */}
              <p className="lede mt-6 text-lg md:text-xl text-secondary font-light leading-relaxed">
                Everything a journalist, podcast host, or event organizer needs to profile Dr. Refai or book him for a story. Short and long bios, print-resolution headshots, verified credentials, topics available for comment, and a direct-inquiry form.
              </p>
              {/* Primary + secondary CTA */}
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  href="/media-inquiries"
                  className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-8 text-sm font-medium hover:bg-secondary transition-colors"
                >
                  Request an interview
                </Link>
                <a
                  href="/img/headshot-large.jpg"
                  download
                  className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
                >
                  Download press headshot
                </a>
              </div>
              {/* Proof strip: press features + credentials */}
              <ul aria-label="Credentials and press" className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-muted-foreground list-none p-0">
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>Board certified (ABPN)</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>FAPA</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li>Quoted in The Epoch Times, EatingWell, and MedStory</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li>Forbes Business Council member</li>
              </ul>
              <p className="mt-6 text-xs text-muted-foreground font-light leading-relaxed max-w-2xl">
                Educational commentary only. Dr. Refai doesn't provide individualized medical guidance through media appearances, and contact through this page doesn't establish a physician-patient relationship.
              </p>
            </div>
            <div className="md:col-span-5 order-1 md:order-2">
              <figure className="relative m-0">
                <img
                  src={MICROPHONE_SRC}
                  alt={MICROPHONE_ALT}
                  width={1200}
                  height={900}
                  className="w-full h-auto object-cover aspect-[4/3] shadow-sm"
                  loading="eager"
                  fetchPriority="high"
                />
              </figure>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 px-4 md:px-8 container mx-auto max-w-5xl space-y-20">
        {/* Short Bio */}
        <div className="short-bio grid md:grid-cols-[1fr_2fr] gap-12">
          <div>
            <h2 className="text-2xl font-serif text-primary border-b border-border pb-4 mb-6">
              Short Bio
            </h2>
          </div>
          <div className="bg-[#EFECE6] p-6 text-base font-light text-secondary leading-relaxed">
            Shariq Refai, MD, MBA, is a board-certified psychiatrist, founder
            of shrinkMD and shrinQ, creator of the Unstuck app, author, and mental health
            educator based in Jacksonville, Florida.
          </div>
        </div>

        {/* Extended Bio */}
        <div className="extended-bio grid md:grid-cols-[1fr_2fr] gap-12">
          <div>
            <h2 className="text-2xl font-serif text-primary border-b border-border pb-4 mb-6">
              Extended Bio
            </h2>
          </div>
          <div className="space-y-5 text-secondary font-light leading-relaxed">
            <p>
              Shariq Refai, MD, MBA, is a board-certified psychiatrist with
              more than fifteen years of experience in psychiatry and mental health care. He holds dual board
              certifications from the American Board of Psychiatry and
              Neurology and the American Board of Sports and Performance
              Psychiatry, and he's a Fellow of the American Psychiatric
              Association.
            </p>
            <p>
              Dr. Refai is the founder and CEO of shrinkMD, an independent
              multistate telepsychiatry company expanding access to
              psychiatric care.
              He's the creator of Unstuck, a mental wellness app for
              reflection, journaling, and emotional awareness. He's the
              author of three upcoming books on anxiety, overthinking, and
              the patterns that keep people mentally stuck, published through
              shrinkMD Publishing.
            </p>
            <p>
              His professional experience in sports and performance
              psychiatry includes affiliations involving the NFL Substance
              Abuse Program and the Jacksonville Jaguars. He earned his MD from St. George's
              University School of Medicine, completed psychiatry residency
              at the University of Hawaii and at John Peter Smith Hospital,
              and holds an MBA from Duke University and certificates in
              Electroconvulsive Therapy and Obesity Medicine from Columbia
              University.
            </p>
            <p>
              Dr. Refai is a member of the Forbes Business Council, an active
              voice in digital mental health, and a frequent commentator on
              psychiatry and emotional health. He's based in Jacksonville,
              Florida.
            </p>
          </div>
        </div>

        {/* Boilerplate */}
        <div className="boilerplate grid md:grid-cols-[1fr_2fr] gap-12">
          <div>
            <h2 className="text-2xl font-serif text-primary border-b border-border pb-4 mb-6">
              Boilerplate
            </h2>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              For press releases
            </p>
          </div>
          <div className="copy-friendly bg-white border border-border p-6 text-base font-light text-secondary leading-relaxed">
            Shariq Refai, MD, MBA, is a board-certified psychiatrist, founder
            and CEO of shrinkMD, founder of shrinQ, creator of the Unstuck app, and author. He
            holds dual board certifications in psychiatry and
            sports/performance psychiatry, an MBA from Duke, and certificates
            from Columbia. He's a Fellow of the American Psychiatric
            Association and a member of the Forbes Business Council. Based in
            Jacksonville, Florida.
          </div>
        </div>

        {/* Areas of Commentary */}
        <div className="topics-available grid md:grid-cols-[1fr_2fr] gap-12">
          <div>
            <h2 className="text-2xl font-serif text-primary border-b border-border pb-4 mb-6">
              Areas of Commentary
            </h2>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Educational topics, not individualized medical advice
            </p>
          </div>
          <ul className="space-y-3 text-secondary font-light leading-relaxed">
            {[
              "Anxiety, overthinking, and emotional health as cultural and educational topics",
              "The high-performer pattern, pressure, and burnout in modern work",
              "Sports and performance psychiatry as a field, including general experience working with elite environments",
              "The state of telepsychiatry and digital mental health platforms",
              "Public understanding of psychiatry and the limits of popular mental health commentary",
              "Workplace mental health culture and psychological safety",
              "Mental health policy and access to care in the United States",
              "The intersection of psychiatry, technology, and consumer wellness products",
            ].map((topic, i) => (
              <li
                key={i}
                className="border-l-2 border-accent pl-4 py-1 text-base"
              >
                {topic}
              </li>
            ))}
          </ul>
        </div>

        {/* Approved Credentials & Press Links */}
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-serif text-primary border-b border-border pb-4 mb-6">
              Approved Credentials
            </h2>
            <ul className="space-y-3 text-sm text-secondary font-light leading-relaxed">
              <li>Board-Certified, American Board of Psychiatry and Neurology</li>
              <li>Board-Certified, American Board of Sports and Performance Psychiatry</li>
              <li>Fellow, American Psychiatric Association (FAPA)</li>
              <li>Founder &amp; CEO, shrinkMD</li>
              <li>Creator &amp; Developer, Unstuck App</li>
              <li>MBA, Duke University</li>
              <li>MD, St. George's University School of Medicine</li>
              <li>Certificate in Electroconvulsive Therapy, Columbia University</li>
              <li>Certificate in Obesity Medicine, Columbia University</li>
              <li>Member, Forbes Business Council</li>
            </ul>
          </div>

          <div className="press-links">
            <h2 className="text-2xl font-serif text-primary border-b border-border pb-4 mb-6">
              Press &amp; Professional Links
            </h2>
            <ul className="space-y-6">
              <li>
                <a
                  href="https://councils.forbes.com/profile/Shariq-Refai-MD-MBA-Board-Certified-Psychiatrist-%7C-Founder-CEO-shrinkMD-shrinkMD/c1649312-6792-4cd3-b764-df4c393ec6c7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1 block">
                    Forbes Business Council
                  </span>
                  <span className="text-primary font-medium group-hover:text-accent transition-colors block text-base">
                    Official Profile
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/in/shariqrefai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1 block">
                    LinkedIn
                  </span>
                  <span className="text-primary font-medium group-hover:text-accent transition-colors block text-base">
                    Professional Profile
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.doximity.com/pub/shariq-refai-md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1 block">
                    Doximity
                  </span>
                  <span className="text-primary font-medium group-hover:text-accent transition-colors block text-base">
                    Provider Profile
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Downloadable Assets */}
        <div className="assets">
          <h2 className="text-2xl font-serif text-primary border-b border-border pb-4 mb-4">
            Downloadable Assets
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-8 max-w-3xl">
            High-resolution photos and approved promotional assets are
            available for press use. Please credit "Shariq Refai, MD, MBA"
            with link to shariqrefai.com when used.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <a
              href={headshot}
              download="refai-headshot-hi-res.jpg"
              className="group relative block aspect-[3/4] bg-muted overflow-hidden"
            >
              <img
                src={headshot}
                alt="Headshot of Shariq Refai, MD, MBA, available for download."
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" width={800} height={800} loading="lazy" decoding="async"/>
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-white text-sm font-medium uppercase tracking-wider">
                  Download Headshot
                </span>
              </div>
            </a>
            <a
              href={speakingPhoto}
              download="refai-stage.jpg"
              className="group relative block aspect-[3/4] bg-muted overflow-hidden"
            >
              <img
                src={speakingPhoto}
                alt="On-stage photo of Shariq Refai, MD, MBA, available for download."
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" width={1000} height={1500} loading="lazy" decoding="async"/>
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-white text-sm font-medium uppercase tracking-wider">
                  Download Stage Photo
                </span>
              </div>
            </a>
            <a
              href={bookCover}
              download="your-mind-is-full-of-shit-cover.png"
              className="group relative block aspect-[3/4] bg-muted overflow-hidden p-4"
            >
              <img
                src={bookCover}
                alt="Cover image of Your Mind Is Full of Sh*t for press use."
                className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105 shadow-xl" width={500} height={781} loading="lazy" decoding="async"/>
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-white text-sm font-medium uppercase tracking-wider">
                  Download Book Cover
                </span>
              </div>
            </a>
          </div>
        </div>

        {/* Inquiries CTA */}
        <div className="inquiries bg-secondary text-secondary-foreground p-12 text-center max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl mb-4 text-white">
            Media Inquiries
          </h2>
          <p className="text-secondary-foreground/85 font-light mb-8 max-w-xl mx-auto leading-relaxed">
            For interview requests, podcast appearances, expert commentary, or
            background quotes, use the{" "}
            <Link
              href="/media-inquiries"
              className="text-white underline underline-offset-4 hover:text-white/80"
            >
              Media &amp; Press Inquiries page
            </Link>
            {" "}or email{" "}
            <a
              href="mailto:media@shariqrefai.com"
              className="text-white underline underline-offset-4 hover:text-white/80"
            >
              media@shariqrefai.com
            </a>
            . Inquiries are reviewed within three business days.
          </p>
          <Link
            href="/media-inquiries"
            className="inline-flex h-12 items-center justify-center bg-white text-secondary px-8 text-sm font-medium hover:bg-white/90 transition-colors"
          >
            Submit Inquiry
          </Link>
        </div>
      </section>

      {/* Recent Press Coverage + Quote Library + Reviewer Credentials + Asset Use + Reprint + Press Desk Hours */}
      <section className="container mx-auto max-w-3xl px-4 md:px-8 pb-20 md:pb-24 flex flex-col gap-16">
        <div className="recent-coverage">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Featured In
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            Selected press features where Dr. Refai has appeared as an expert
            source. See the full{" "}
            <Link
              href="/press"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              press archive
            </Link>
            {" "}for additional coverage, or use the{" "}
            <Link
              href="/media-inquiries"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              Media &amp; Press Inquiries page
            </Link>
            {" "}for interview requests.
          </p>
          <ul className="space-y-5">
            {PRESS_FEATURES.map((feature) => (
              <li key={feature.url}>
                <a
                  href={feature.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1 block">
                    {feature.publisher}
                  </span>
                  <span className="text-primary font-medium group-hover:text-accent transition-colors block text-base leading-snug">
                    {feature.headline}
                  </span>
                </a>
                <span className="text-sm text-secondary font-light block mt-1">
                  {feature.topic}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="quote-library">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Quote Library
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-8">
            Pre-approved pull quotes for press use. Each is available for
            direct attribution to Shariq Refai, MD, MBA. Please credit the
            source URL when quoting.
          </p>
          <div className="grid gap-6">
            {[
              {
                quote:
                  "The brain isn't getting louder at night. It's getting audible. All day, work and motion drown out the noise the nervous system has been making in the background. The second you stop moving, that signal finally reaches you.",
                cite: "On bedtime anxiety",
              },
              {
                quote:
                  "High performers don't feel anxious despite their success. They often feel anxious because of the same wiring that produced it. The same nervous system that scans for threat, anticipates outcomes, and refuses to rest until the work is done is a powerful engine for output and a difficult engine to live inside.",
                cite: "On high-functioning anxiety",
              },
              {
                quote:
                  "Thinking harder about anxiety almost never reduces it. The brain treats analytical thinking as a form of safety seeking, which means the more you analyze an anxious thought, the more reliable analyzing becomes as a coping pattern.",
                cite: "On overthinking",
              },
              {
                quote:
                  "Most popular mental health advice is calm-brain advice given to a panicked brain. The principle is correct. The execution most people are taught is wrong.",
                cite: "On the limits of popular advice",
              },
              {
                quote:
                  "Modern psychiatry isn't what most people think. The field has changed enormously over the last twenty years, and most of the change is good. The public conversation hasn't caught up.",
                cite: "On the state of modern psychiatry",
              },
              {
                quote:
                  "Convenience matters, and the access problem in psychiatry was real. Convenience isn't care.",
                cite: "On digital mental health",
              },
            ].map((q, i) => (
              <blockquote
                key={i}
                className="border-l-2 border-accent pl-6 py-2"
              >
                <p className="font-serif text-lg text-primary italic leading-relaxed mb-3">
                  "{q.quote}"
                </p>
                <cite className="text-xs tracking-widest uppercase text-muted-foreground not-italic">
                  Shariq Refai, MD, MBA &middot; {q.cite}
                </cite>
              </blockquote>
            ))}
          </div>
        </div>

        <div className="reviewer-credentials">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Editorial Reviewer Credentials
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            Beyond his work at shariqrefai.com, Dr. Refai serves as the
            medical editor and clinical reviewer for four independent
            editorial publications:
          </p>
          <ul className="list-disc pl-6 space-y-3 text-secondary font-light leading-relaxed mb-4">
            <li>
              <a
                href={withNetworkUtm("https://anxietyresource.org/", "media_page")}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                <strong>AnxietyResource.org</strong>
              </a>
              , a clinically reviewed editorial publication on anxiety with
              a 50-entry plain-language glossary and complete state-by-state
              directories.
            </li>
            <li>
              <a
                href={withNetworkUtm("https://depressionresource.org/", "media_page")}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                <strong>DepressionResource.org</strong>
              </a>
              , a long-form patient education site on depression including
              screening tools, safety plan templates, and crisis resources.
            </li>
            <li>
              <a
                href={withNetworkUtm("https://anxietyresearch.org/", "media_page")}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                <strong>AnxietyResearch.org</strong>
              </a>
              , an editorial publication translating current anxiety research
              into plain-language summaries for general readers.
            </li>
            <li>
              <a
                href={withNetworkUtm("https://psychiatryrx.org/", "media_page")}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                <strong>PsychiatryRx.org</strong>
              </a>
              , a plain-language reference site publishing psychiatrist-reviewed
              guides to psychiatric and sleep medications, sourced from FDA
              labeling and clinical guidelines.
            </li>
          </ul>
          <p className="text-secondary font-light leading-relaxed">
            Every article on all four sites carries Dr. Refai's "Authored and
            editorially reviewed by" credit with a publish and
            last-reviewed date. His role is editorial review for clinical
            accuracy. See the{" "}
            <Link
              href="/medical-review-board"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              Medical Review Board page
            </Link>
            {" "}for reviewer roles and standards.
          </p>
        </div>

        <div className="press-identifiers">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Identifiers (for press attribution)
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-4">
            Authoritative identifiers journalists and editors can use to
            verify authorship and credentials when citing or attributing
            Dr. Refai.
          </p>
          <ul className="list-disc pl-6 space-y-3 text-secondary font-light leading-relaxed">
            <li>
              ORCID iD:{" "}
              <a
                href="https://orcid.org/0009-0009-1090-4373"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                <img
                  src="/orcid-id-icon.svg"
                  alt=""
                  width={14}
                  height={14}
                  aria-hidden="true"
                  style={{ display: "inline-block", verticalAlign: "-2px", marginRight: "0.3em" }}
                />
                0009-0009-1090-4373
              </a>
            </li>
            <li>
              Wikidata:{" "}
              <a
                href="https://www.wikidata.org/wiki/Q139822307"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                Q139822307
              </a>
            </li>
            <li>
              NPI:{" "}
              <a
                href="https://npiregistry.cms.hhs.gov/provider-view/1467680660"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
              >
                1467680660
              </a>
            </li>
          </ul>
        </div>

        <div className="asset-use">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Asset Use and Credit Lines
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-3">
            For headshots and other approved press images, please credit:
          </p>
          <p className="mb-6 p-4 bg-muted/40 border border-border font-mono text-sm text-primary">
            Photo courtesy of Shariq Refai, MD, MBA / shariqrefai.com
          </p>
          <p className="text-secondary font-light leading-relaxed mb-3">
            For quoted material, please credit:
          </p>
          <p className="mb-6 p-4 bg-muted/40 border border-border font-mono text-sm text-primary">
            Shariq Refai, MD, MBA, board-certified psychiatrist and founder
            of shrinkMD. Source: [URL of the article you're quoting]
          </p>
          <p className="text-secondary font-light leading-relaxed mb-3">
            For book references, please credit:
          </p>
          <p className="p-4 bg-muted/40 border border-border font-mono text-sm text-primary">
            From [Book Title] by Shariq Refai, MD, MBA, shrinkMD Publishing.
          </p>
        </div>

        <div className="reprint-syndication">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Reprint and Syndication
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            Article reprints, syndication, and translation requests should
            go through the{" "}
            <Link
              href="/contact"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              contact form
            </Link>{" "}
            with "Media &amp; Press" selected. Republication of full
            articles requires written permission. Quoting up to 200 words
            with attribution and a link is permitted under fair use.
          </p>
        </div>

        <div className="press-desk-hours">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">
            Press Desk Hours
          </h2>
          <p className="text-secondary font-light leading-relaxed">
            Media inquiries are reviewed Monday through Friday, 9:00 AM to
            6:00 PM Eastern. For deadline-sensitive requests, please mark
            "URGENT" in the subject line. Same-day responses aren't
            guaranteed but are attempted where possible.
          </p>
        </div>
      </section>
    
      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="media_next_step"
            destinations={["shrinkmd", "shrinkopedia", "shrinq"]}
          />
        </div>
      </section>
    </SiteLayout>
  );
}
